import React, { useCallback, useMemo, useState } from 'react';
import { Box, Typography, IconButton, TextField, Chip, Paper, Stack } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

export interface AvailabilitySlot {
  date: string; // YYYY-MM-DD
  start_time: string; // HH:MM
  end_time: string; // HH:MM
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Convert Date to YYYY-MM-DD using local timezone (not UTC)
const dateKey = (d: Date) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/** Builds a 6-row month grid (Monday-first) for the given month, including leading/trailing
 * days from adjacent months so every week row is complete. */
const buildMonthGrid = (year: number, month: number): Date[] => {
  const firstOfMonth = new Date(year, month, 1);
  const firstWeekday = (firstOfMonth.getDay() + 6) % 7; // 0 = Monday
  const start = new Date(year, month, 1 - firstWeekday);
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
};

interface Props {
  slots: AvailabilitySlot[];
  onSlotsChange: (slots: AvailabilitySlot[]) => void;
  notes: string;
  onNotesChange: (notes: string) => void;
  /** Dates after this are not selectable (the certificate's expiry). */
  expiryDate: Date | null;
  /** Slots to highlight as already good for the other party (e.g. the provider); selecting such a day prefills its times. */
  markedSlots?: AvailabilitySlot[];
}

// Month calendar + per-date time rows + notes — shared by the public /availability/<token> page
// (tenant/provider) and the landlord's manual entry in a flow.
const AvailabilityPicker: React.FC<Props> = ({ slots, onSlotsChange, notes, onNotesChange, expiryDate, markedSlots = [] }) => {
  const today = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, []);
  const [viewDate, setViewDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1));

  // Only dates between today and the certificate's expiry make sense to offer.
  const isSelectable = useCallback((d: Date) => {
    if (d < today) return false;
    if (expiryDate && d > expiryDate) return false;
    return true;
  }, [today, expiryDate]);

  // Clicking a date toggles it: select if not already chosen, deselect (remove its slot) if it is.
  const toggleSlotForDate = (date: Date) => {
    const key = dateKey(date);
    const preset = markedSlots.find(s => s.date === key);
    onSlotsChange(slots.some(s => s.date === key)
      ? slots.filter(s => s.date !== key)
      : [...slots, { date: key, start_time: preset?.start_time || '09:00', end_time: preset?.end_time || '12:00' }]);
  };

  const updateSlot = (index: number, field: keyof AvailabilitySlot, value: string) => {
    onSlotsChange(slots.map((s, i) => (i === index ? { ...s, [field]: value } : s)));
  };

  const grid = buildMonthGrid(viewDate.getFullYear(), viewDate.getMonth());
  const monthLabel = viewDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

  return (
    <>
      <Paper variant="outlined" sx={{ p: 1.6, mb: 3, maxWidth: 360, mx: 'auto' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
          <IconButton size="small" onClick={() => setViewDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1))}>
            <ChevronLeftIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <Typography variant="subtitle2" fontWeight={600}>{monthLabel}</Typography>
          <IconButton size="small" onClick={() => setViewDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1))}>
            <ChevronRightIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Stack>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.3, mb: 0.3 }}>
          {WEEKDAYS.map(w => (
            <Typography key={w} variant="caption" align="center" color="text.secondary" sx={{ fontSize: '0.7rem' }}>{w}</Typography>
          ))}
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.3 }}>
          {grid.map((d, i) => {
            const inMonth = d.getMonth() === viewDate.getMonth();
            const selectable = isSelectable(d);
            const hasSlot = slots.some(s => s.date === dateKey(d));
            const marked = markedSlots.some(s => s.date === dateKey(d));
            return (
              <Box
                key={i}
                onClick={() => selectable && toggleSlotForDate(d)}
                sx={{
                  aspectRatio: '1',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 0.75,
                  fontSize: 12,
                  cursor: selectable ? 'pointer' : 'default',
                  opacity: inMonth ? 1 : 0.35,
                  color: selectable ? 'text.primary' : 'text.disabled',
                  bgcolor: hasSlot ? 'primary.main' : marked ? 'success.light' : 'transparent',
                  ...(marked && { border: '2px solid', borderColor: 'success.main', fontWeight: 700 }),
                  ...(hasSlot && { color: 'primary.contrastText' }),
                  '&:hover': selectable ? { bgcolor: hasSlot ? 'primary.dark' : 'action.hover' } : {},
                }}
              >
                {d.getDate()}
              </Box>
            );
          })}
        </Box>
        {markedSlots.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Outlined days are times the engineer has said work for them.
          </Typography>
        )}
      </Paper>

      {slots.length > 0 && (
        <Stack spacing={1.5} sx={{ mb: 3 }}>
          <Typography variant="subtitle2">Your selected times</Typography>
          {slots.map((slot, i) => {
            const [year, month, day] = slot.date.split('-').map(Number);
            const displayDate = new Date(year, month - 1, day);
            return (
              <Stack key={i} direction="row" spacing={1} alignItems="center">
                <Chip label={displayDate.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} size="small" />
                <TextField type="time" size="small" value={slot.start_time} onChange={e => updateSlot(i, 'start_time', e.target.value)} />
                <Typography variant="body2">to</Typography>
                <TextField type="time" size="small" value={slot.end_time} onChange={e => updateSlot(i, 'end_time', e.target.value)} />
                <IconButton size="small" onClick={() => onSlotsChange(slots.filter((_, j) => j !== i))}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
            );
          })}
        </Stack>
      )}

      <TextField
        label="Anything else we should know? (optional)"
        multiline minRows={2} fullWidth sx={{ mb: 3 }}
        value={notes} onChange={e => onNotesChange(e.target.value)}
      />
    </>
  );
};

export default AvailabilityPicker;
